import React, { useState, useEffect } from 'react';
import { getSettings, updateSettings, type Setting } from '../services/api';
import { Box, Typography, Slider, Button, CircularProgress, Alert, TextField, Divider } from '@mui/material';
import { useNotification } from '../context/NotificationContext';

// Helper to find a setting value or return a default
const findValue = (settings: Setting[], key: string, defaultValue: number): number => {
    const setting = settings.find(s => s.key === key);
    return setting ? parseFloat(setting.value) : defaultValue;
};

const marks = [
    { value: 0, label: '0.0' },
    { value: 1, label: '1.0 (預設)' },
    { value: 2, label: '2.0' },
    { value: 3, label: '3.0' },
];

const AlgorithmSettings: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { showNotification } = useNotification();

    // Scoring weights
    const [qualityWeight, setQualityWeight] = useState(1.0);
    const [fairnessWeight, setFairnessWeight] = useState(1.0);
    const [varietyWeight, setVarietyWeight] = useState(1.0);

    // GA Hyperparameters
    const [populationSize, setPopulationSize] = useState(30);
    const [numGenerations, setNumGenerations] = useState(50);


    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setLoading(true);
                const data = await getSettings();
                // Set weights
                setQualityWeight(findValue(data, 'w_quality', 1.0));
                setFairnessWeight(findValue(data, 'w_fairness', 1.0));
                setVarietyWeight(findValue(data, 'w_variety', 1.0));
                // Set GA params
                setPopulationSize(findValue(data, 'ga_population_size', 30));
                setNumGenerations(findValue(data, 'ga_num_generations', 50));

            } catch (err) {
                setError('讀取設定失敗。');
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleSave = async () => {
        const newSettings: Setting[] = [
            { key: 'w_quality', value: String(qualityWeight) },
            { key: 'w_fairness', value: String(fairnessWeight) },
            { key: 'w_variety', value: String(varietyWeight) },
            { key: 'ga_population_size', value: String(populationSize) },
            { key: 'ga_num_generations', value: String(numGenerations) },
        ];
        try {
            setLoading(true);
            await updateSettings(newSettings);
            showNotification('設定已儲存！', 'success');
        } catch (err) {
            showNotification('儲存設定失敗。', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;

    return (
        <Box sx={{p: 2, minWidth: 400}}>
            <Typography variant="h6" gutterBottom>評分權重</Typography>
            
            <Typography gutterBottom><strong>實力評分 (Quality)</strong> 權重</Typography>
            <Typography variant="caption" display="block" gutterBottom>
                數值越高，系統越傾向於產生實力相近的對戰組合。
            </Typography>
            <Slider value={qualityWeight} onChange={(_, v) => setQualityWeight(v as number)} aria-labelledby="quality-slider" valueLabelDisplay="auto" step={0.1} min={0} max={3} marks={marks} />
            
            <Typography gutterBottom sx={{mt: 2}}><strong>公平性 (Fairness)</strong> 權重</Typography>
            <Typography variant="caption" display="block" gutterBottom>
                數值越高，系統越優先讓等待已久的球員上場。
            </Typography>
            <Slider value={fairnessWeight} onChange={(_, v) => setFairnessWeight(v as number)} aria-labelledby="fairness-slider" valueLabelDisplay="auto" step={0.1} min={0} max={3} marks={marks} />

            <Typography gutterBottom sx={{mt: 2}}><strong>多樣性 (Variety)</strong> 權重</Typography>
            <Typography variant="caption" display="block" gutterBottom>
                數值越高，系統越傾向於產生沒合作過的隊友或對手組合。
            </Typography>
            <Slider value={varietyWeight} onChange={(_, v) => setVarietyWeight(v as number)} aria-labelledby="variety-slider" valueLabelDisplay="auto" step={0.1} min={0} max={3} marks={marks} />

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" gutterBottom>演算法速度</Typography>
            <Box sx={{display: 'flex', gap: 2, mt: 2}}>
                <TextField
                    label="族群數量 (Population Size)"
                    type="number"
                    value={populationSize}
                    onChange={(e) => setPopulationSize(Number(e.target.value))}
                    helperText="每次迭代計算的組合數量。數值越低速度越快，但可能影響品質。"
                    fullWidth
                />
                <TextField
                    label="演化代數 (Generations)"
                    type="number"
                    value={numGenerations}
                    onChange={(e) => setNumGenerations(Number(e.target.value))}
                    helperText="演算法的計算深度。數值越低速度越快，但可能影響品質。"
                    fullWidth
                />
            </Box>

            <Box mt={4} textAlign="right">
                <Button variant="contained" onClick={handleSave} disabled={loading}>
                    儲存設定
                </Button>
            </Box>
        </Box>
    );
};

export default AlgorithmSettings;
